<template>
  <div v-if="providerName" class="embeds">
    <div class="wrapper">
      <CookieConsentCheck :cookie-type="cookieType" class="inner" @allowed="onCookieConsentAllowed">
        <NpoPlayer
          v-if="providerName === 'AVROTROS' && OEmbed.html"
          :video-id="extractVideoIdFromIFrameSrc(OEmbed.html)"
          :duration="props.staticData?.item?.duration"
          :show-controls="true"
          :show-embed="true"
          :style="computedAspectRatioStyle"
          :initial-state="props.initialStatus"
          @loaded="onNpoPlayerLoaded"
        />

        <!--  INOS CUSTOM VIDEO PLAYER-->
        <VideoPlayer v-if="providerName === 'INOS' && OEmbed" :poster="poster" :src="OEmbed.src" />

        <div
          v-else-if="OEmbed.html && providerName != 'INOS' && providerName != 'AVROTROS'"
          :class="['provider', providerClass]"
          :style="computedAspectRatioStyle"
          v-html="processedEmbedHtml"
        />

        <iframe
          v-else-if="!OEmbed.html"
          :class="[providerClass, 'iframe']"
          :src="props.staticData?.item?.embed?.url"
          height="100%"
          width="100%"
          allowfullscreen
          :title="props.staticData?.item?.caption"
          allow="accelerometer; encrypted-media *"
          :style="computedAspectRatioStyle"
          loading="lazy"
        ></iframe>

        <Caption
          v-if="props.staticData?.item?.caption"
          ref="captionElementRef"
          :content="props.staticData?.item?.caption"
          :max-lines="3"
          class="embed-caption"
          :class="!showCaption ? 'hide-caption' : null"
          :has-padding="captionHasPadding"
        />
      </CookieConsentCheck>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IEmbed, TPlayerStatus } from '~~/types/local_types';
import type { ICmsContentEmbeds } from '~~/types/_cms-types';
const { directusAssetsUrl } = useRuntimeConfig().public;

const { isNumber } = formatUtils();
const providerName = ref<string | undefined>();

const noCookiesProviders = ['youtube', 'avrotros', 'inos'];
const socialMediaProviders = ['twitter', 'instagram', 'tiktok', 'bluesky'];
const emit = defineEmits(['loaded']);

const allowedCookies = ref(false);

interface IItem {
  item?: ICmsContentEmbeds;
}

interface IProps {
  staticData?: IItem;
  caption?: string;
  oembed?: IEmbed['oembed'];
  showCaption?: boolean;
  captionHasPadding?: boolean;
  initialStatus?: TPlayerStatus; // only for npo-video
}

const props = withDefaults(defineProps<IProps>(), {
  staticData: undefined,
  caption: undefined,
  oembed: undefined,
  showCaption: true,
  captionHasPadding: false,
  initialStatus: 'paused',
});

const OEmbed = props.staticData?.item?.embed?.oembed;

const poster = computed(() => {
  if (props.staticData.item?.videoImageId) {
    return `${directusAssetsUrl}${props.staticData?.item?.videoImageId}.webp?width=720&height=405&fit=cover`;
  } else {
    return `${directusAssetsUrl}${OEmbed.imageId}.webp?width=720&height=405&fit=cover`;
  }
});

// <script> tags inside v-html never execute, so they're stripped here and
// re-injected via useHead() in onCookieConsentAllowed() once cookie consent is granted.
const extractAndRemoveScripts = () => {
  if (!OEmbed.html)
    return {
      cleanedHtml: OEmbed.html,
      scriptSources: [],
    };

  // Regex to find <script> tags and capture src attributes
  const scriptRegex = /<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*>.*?<\/script>/gi;

  // Extract all script src URLs
  const scriptSources: string[] = [];
  const htmlString = OEmbed.html.replace(scriptRegex, (match, src) => {
    scriptSources.push(src);
    return ''; // Remove the script tag
  });

  return {
    cleanedHtml: htmlString.trim(), // Remove extra spaces
    scriptSources,
  };
};

if (OEmbed) {
  if (OEmbed.providerName) {
    providerName.value = OEmbed.providerName;
  } else if (OEmbed.providerUrl === 'Bluesky Social') {
    // bluesky doesn't seem to have providerName, it does have a url containing the name value
    providerName.value = 'bluesky';
    const { cleanedHtml, scriptSources } = extractAndRemoveScripts();

    if (!OEmbed.scriptData) {
      OEmbed.scriptData = {
        sources: [],
      };
    }
    OEmbed.scriptData.sources?.push(...scriptSources);
    OEmbed.html = cleanedHtml;
  }
}

// Resolved once at setup, not reactive — fine since staticData is only ever set on mount.
const providerClass = providerName.value && `provider-${providerName.value.toLowerCase()}`;

const maxWidth = OEmbed?.width
  ? isNumber(OEmbed.width)
    ? `${OEmbed.width}px`
    : `${OEmbed.width}`
  : undefined;

const providerNameLower = OEmbed?.providerName?.toLowerCase();
const noCookies = providerNameLower ? noCookiesProviders.includes(providerNameLower) : undefined;
const socialMedia = providerNameLower
  ? socialMediaProviders.includes(providerNameLower)
  : undefined;

// make sure all iframes are lazy
const lazyAccessibleIFrame = (htmlString: string) => {
  const title = props.staticData?.item?.embed?.oembed?.title || 'embedded content';
  const lazyAttr = htmlString.includes('loading="lazy"') ? '' : 'loading="lazy" ';
  return htmlString.replace(/<iframe\s+/i, `<iframe ${lazyAttr}title="${title}"`);
};

const processedEmbedHtml = computed(() => OEmbed.html && lazyAccessibleIFrame(OEmbed.html));

const extractVideoIdFromIFrameSrc = (iframeString: string) => {
  const srcMatch = iframeString.match(/src="([^"]+)"/);

  const url = srcMatch && srcMatch[1] ? srcMatch[1] : undefined;

  if (url) {
    return url.split('/').at(-1);
  }

  return url;
};

const cookieType: 'social' | 'overige' | 'noCookie' = socialMedia
  ? 'social'
  : noCookies
    ? 'noCookie'
    : 'overige';

const computedAspectRatioStyle = computed(() => {
  return {
    'aspect-ratio': OEmbed.aspectRatio,
  };
});

const initProviderWidget = (provider: string) => {
  switch (provider) {
    case 'bluesky':
      if (window.bluesky) {
        window.bluesky.scan();
      }
      break;
    case 'twitter':
      if (window.twttr) {
        window.twttr.widgets.load();
      }
      break;
    case 'tiktok':
      if (window.tiktokEmbed) {
        const ttOnPage = Array.from(document.querySelectorAll('blockquote.tiktok-embed'));
        window.tiktokEmbed.lib.render(ttOnPage);
      }
      break;
    case 'jotform':
      if (window.jotformEmbedHandler) {
        const jfOnPage = Array.from(document.querySelectorAll('.provider-jotform'));

        jfOnPage.forEach((element) => {
          const iframe = element.querySelector('iframe');
          const iframeId = iframe?.id;

          // by default the jotform iFrames parent scrolls to the top on load, this prevents that
          if (iframe) {
            iframe.onload = () => {
              return false;
            };
          }

          if (window.jotformEmbedHandler) {
            window.jotformEmbedHandler(`#${iframeId}`);
          }

          window.requestAnimationFrame(() => {
            if (window.jotformEmbedHandler) {
              window.jotformEmbedHandler(`#${iframeId}`);
            }
          });
        });
      }
      break;
    default:
      break;
  }
};

const onCookieConsentAllowed = (allowed: boolean) => {
  allowedCookies.value = allowed;

  if (allowedCookies.value) {
    if (OEmbed.scriptData && OEmbed.scriptData.sources) {
      OEmbed.scriptData?.sources.forEach((src) => {
        useHead({
          script: [
            {
              type: 'text/javascript',
              src,
              async: true,
              defer: true,
              body: true,
              onload: () => {
                if (OEmbed && providerName.value) {
                  initProviderWidget(providerName.value.toLowerCase());
                }
              },
            },
          ],
          once: true,
        });
      });
    }
  }
};
// check on mounted
onMounted(() => {
  nextTick(() => {
    if (allowedCookies.value) {
      initProviderWidget(OEmbed.providerName.toLowerCase());
    }
  });
});

// check in load more on scroll
onActivated(async () => {
  await nextTick();
  if (OEmbed?.providerName && allowedCookies.value) {
    initProviderWidget(OEmbed.providerName.toLowerCase());
  }
});

const onNpoPlayerLoaded = (meta: SourceConfig) => {
  emit('loaded', meta);
};
</script>

<style scoped lang="postcss">
.embeds {
  position: relative;
}

.wrapper {
  position: relative;
}

.inner {
}

.provider {
  line-height: 0;
  display: flex;
  justify-content: center;
  margin: 0 auto !important;

  :deep(*) {
    margin: 0 auto !important;
  }

  + .caption {
    display: flex;
    width: 100%;
    margin-left: auto !important;
    margin-right: auto !important;
  }
}

.provider-twitter {
  :deep(.twitter-tweet) {
    opacity: 0;
    transition: opacity 0.25s ease;

    &.twitter-tweet-rendered {
      opacity: 1;
    }
  }
}

.provider-tiktok {
  padding: 0;
  height: auto;
  display: contents;

  :deep(blockquote) {
    margin: 0;

    > *:not(iframe) {
      visibility: hidden;
    }
  }

  + .caption {
    max-width: 325px;
  }
}

.provider-spotify {
  max-width: v-bind(maxWidth);

  + .caption {
    max-width: v-bind(maxWidth);
  }
}

.provider-facebook {
  display: flex;
  justify-content: center;
  margin: 0 auto;

  :deep(iframe),
  :deep(.fb-post) {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
}

.provider-facebook,
.provider-instagram,
.provider-twitter,
.provider-bluesky {
  max-width: 550px;

  + .caption {
    max-width: 550px;
  }
}

.provider-avrotros,
.provider-youtube {
  max-width: unset;

  :deep(iframe) {
    width: 100%;
    height: 100%;
  }

  + .caption {
    max-width: 100%;
  }
}

.provider-flourish {
  :deep(iframe) {
    height: auto;
  }
}

.embed-caption {
  &.hide-caption {
    display: none;
  }
}
</style>
